import {useRef} from 'react';
import {Upload, FileText, Download} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Label} from '@common-web/components/ui/label';
import {AsyncCombobox} from '@common-web/components/ui/combobox';
import {TEMPLATE_COLUMNS} from './types';
import {downloadTemplate} from './utils';

type GroupOption = {groupId: string; name: string};

type UploadStepProps = {
  urlGroupId: string | undefined;
  selectedGroupId: string;
  selectedGroupName: string;
  onGroupChange: (groupId: string, name: string) => void;
  groupOptions: GroupOption[];
  onGroupSearch: (search: string) => void;
  isLoadingGroups: boolean;
  isDragging: boolean;
  onDragStateChange: (dragging: boolean) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileSelect: (file: File) => void;
  file: File | null;
  totalRows: number;
  rawRowCount: number;
};

export function UploadStep({
  urlGroupId,
  selectedGroupId,
  selectedGroupName,
  onGroupChange,
  groupOptions,
  onGroupSearch,
  isLoadingGroups,
  isDragging,
  onDragStateChange,
  onDrop,
  onFileSelect,
  file,
  totalRows,
  rawRowCount,
}: UploadStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      {!urlGroupId && (
        <Card className='border-0 shadow-lg bg-white overflow-hidden mb-6'>
          <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
            <CardTitle className='text-lg font-semibold text-brand-navy'>Group</CardTitle>
          </CardHeader>
          <CardContent className='p-6'>
            <div className='max-w-sm space-y-2'>
              <Label htmlFor='groupId'>Group *</Label>
              <AsyncCombobox
                id='groupId'
                value={selectedGroupId}
                selectedLabel={selectedGroupName}
                setValue={onGroupChange}
                onSearch={onGroupSearch}
                items={groupOptions.map((g) => ({label: g.name, value: g.groupId}))}
                isLoading={isLoadingGroups}
                message='Select a group...'
                placeholder='Search groups by name...'
                emptyMessage='No groups found.'
                allowClear
              />
            </div>
          </CardContent>
        </Card>
      )}

      <Card className='border-0 shadow-lg bg-white overflow-hidden mb-6'>
        <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
          <div className='flex items-center justify-between'>
            <CardTitle className='text-lg font-semibold text-brand-navy'>
              Download Template
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className='p-6'>
          <div className='flex items-center justify-between p-4 rounded-lg bg-brand-blue/5 border border-brand-blue/10'>
            <div>
              <p className='text-sm font-medium text-brand-navy'>
                Member Import Template
              </p>
              <p className='text-xs text-gray-500 mt-1'>
                CSV template with all supported fields: {TEMPLATE_COLUMNS.join(', ')}
              </p>
            </div>
            <Button variant='outline' onClick={downloadTemplate}>
              <Download className='h-4 w-4 mr-2' />
              Download CSV Template
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className='border-0 shadow-lg bg-white overflow-hidden'>
        <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
          <CardTitle className='text-lg font-semibold text-brand-navy'>
            Upload File
          </CardTitle>
        </CardHeader>
        <CardContent className='p-6'>
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              isDragging
                ? 'border-brand-blue bg-brand-blue/5'
                : 'border-gray-300 hover:border-brand-blue/50'
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              onDragStateChange(true);
            }}
            onDragLeave={() => onDragStateChange(false)}
            onDrop={onDrop}
          >
            <Upload className='h-12 w-12 text-gray-400 mx-auto mb-4' />
            <p className='text-lg font-medium text-brand-navy mb-2'>
              Drag and drop a CSV file here, or click to browse
            </p>
            <p className='text-sm text-gray-500 mb-4'>
              Accepted: .csv (save Excel files as CSV first)
            </p>
            <input
              ref={fileInputRef}
              type='file'
              accept='.csv,text/csv'
              className='sr-only'
              onChange={(e) => {
                const selectedFile = e.target.files?.[0];
                if (selectedFile) onFileSelect(selectedFile);
                e.target.value = '';
              }}
            />
            <Button
              type='button'
              variant='outline'
              onClick={() => fileInputRef.current?.click()}
            >
              Choose File
            </Button>
          </div>
          {file && (
            <div className='flex items-center gap-3 p-3 rounded-lg bg-gray-50 mt-4'>
              <FileText className='h-5 w-5 text-brand-blue' />
              <div>
                <p className='text-sm font-medium'>{file.name}</p>
                <p className='text-xs text-gray-500'>
                  {totalRows} data row{totalRows !== 1 ? 's' : ''} found
                  {rawRowCount !== totalRows &&
                    ` (${rawRowCount} total, ${rawRowCount - totalRows} empty)`}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
